import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getNotificationSettings,
  getUnreadCount,
  markNotificationsRead,
  removeNotification,
  updateNotificationSettings,
  type NotificationCategory,
  type NotificationSettings,
} from "@/api/notifications";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (userId: string, category: NotificationCategory | null) =>
    ["notifications", "list", userId, category ?? "all"] as const,
  unread: (userId: string) => ["notifications", "unread", userId] as const,
  settings: (userId: string) => ["notifications", "settings", userId] as const,
};

export const useNotifications = (userId: string, category: NotificationCategory | null) =>
  useQuery({
    queryKey: notificationKeys.list(userId, category),
    queryFn: () => getNotifications(userId, category),
    enabled: !!userId,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

/** Bell badge; one light request a minute. */
export const useUnreadNotifications = (userId: string) =>
  useQuery({
    queryKey: notificationKeys.unread(userId),
    queryFn: () => getUnreadCount(userId),
    enabled: !!userId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    select: (data) => data.count,
  });

export const useMarkNotificationsRead = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id?: string) => markNotificationsRead(userId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
};

export const useRemoveNotification = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeNotification(userId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
};

export const useNotificationSettings = (userId: string) =>
  useQuery({
    queryKey: notificationKeys.settings(userId),
    queryFn: () => getNotificationSettings(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });

/** Flips switches right away and rolls back if the save fails. */
export const useUpdateNotificationSettings = (userId: string) => {
  const queryClient = useQueryClient();
  const key = notificationKeys.settings(userId);
  return useMutation({
    mutationFn: (patch: Partial<NotificationSettings>) => updateNotificationSettings(userId, patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationSettings>(key);
      if (previous) queryClient.setQueryData(key, { ...previous, ...patch });
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    },
  });
};
